import { Button, Form, Input } from "antd";
import { useChangePasswordMutation } from "api/User/hooks.ts";
import { AxiosError } from "axios";
import { FormCol } from "components/UI/Form/FormCol";
import { FormGrid } from "components/UI/Form/FormGrid";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useCallback, useEffect, useRef } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { ApiErrorResponse } from "types/models";
import { z } from "zod";

const schema = z
    .object({
        current_password: z.string().min(1, "forms.general-validation.current_password_required"),
        password: z.string().min(1, "forms.general-validation.password_required"),
        password_confirmation: z.string().min(1, "forms.general-validation.password_confirmation_required"),
    })
    .refine((d) => d.password === d.password_confirmation, {
        message: "forms.general-validation.passwords_dont_match",
        path: ["password_confirmation"],
    });

type AccountFormData = z.infer<typeof schema>;

interface AccountFormContentProps {
    onRegisterErrorHandler: (handler: (error: AxiosError<ApiErrorResponse>) => void) => void;
}

const AccountFormContent: FC<AccountFormContentProps> = ({ onRegisterErrorHandler }) => {
    const { t: tForm } = usePaldockTranslation("forms.account");
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { control, fieldNames, handleErrorsFromErrorResponse, formState } = useGeneralForm<AccountFormData>();
    const antdForm = Form.useFormInstance();

    useEffect(() => {
        onRegisterErrorHandler(handleErrorsFromErrorResponse);
    }, [onRegisterErrorHandler, handleErrorsFromErrorResponse]);

    return (
        <FormCol>
            <FormGrid cols={1}>
                <FormItem
                    control={control}
                    name={fieldNames.current_password.name}
                    label={tForm("fields.current_password.label")}
                    required
                >
                    <Input.Password />
                </FormItem>
            </FormGrid>
            <FormGrid cols={2}>
                <FormItem
                    control={control}
                    name={fieldNames.password.name}
                    label={tForm("fields.new_password.label")}
                    required
                >
                    <Input.Password placeholder={tFields("password.placeholder")} />
                </FormItem>
                <FormItem
                    control={control}
                    name={fieldNames.password_confirmation.name}
                    label={tForm("fields.confirm_password.label")}
                    required
                >
                    <Input.Password placeholder={tFields("password_confirmation.placeholder")} />
                </FormItem>
            </FormGrid>
            <div className="flex justify-end">
                <Button type="primary" onClick={() => antdForm.submit()} disabled={!formState.isDirty}>
                    {tForm("actions.change_password")}
                </Button>
            </div>
        </FormCol>
    );
};

export const AccountForm: FC = () => {
    const { t: tForm } = usePaldockTranslation("forms.account");
    const errorHandlerRef = useRef<((error: AxiosError<ApiErrorResponse>) => void) | null>(null);

    const changePasswordMutation = useChangePasswordMutation();

    const onSubmit = (data: AccountFormData) => {
        changePasswordMutation.mutate(
            {
                current_password: data.current_password,
                password: data.password,
                password_confirmation: data.password_confirmation,
            },
            {
                onSuccess: () => {
                    toast.success(tForm("messages.password_changed_success"));
                },
                onError: (errorResponse) => {
                    errorHandlerRef.current?.(errorResponse);
                },
            }
        );
    };

    const registerErrorHandler = useCallback((handler: (error: AxiosError<ApiErrorResponse>) => void) => {
        errorHandlerRef.current = handler;
    }, []);

    return (
        <GeneralFormProvider<AccountFormData>
            schema={schema}
            defaultValues={{ current_password: "", password: "", password_confirmation: "" }}
            onSubmit={onSubmit}
        >
            <AccountFormContent onRegisterErrorHandler={registerErrorHandler} />
        </GeneralFormProvider>
    );
};
